import { computed, inject, Injectable, type Signal, signal, type WritableSignal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { TRANSLOCO_CONFIG, type TranslocoConfig, TranslocoService } from '@jsverse/transloco';
import { StorageService } from './storage.service';

const STORAGE_KEY = 'lang';

const LANG_TO_LOCALE: Record<string, string> = {
  it: 'it-IT',
  en: 'en-US'
};

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly translocoService: TranslocoService = inject(TranslocoService);
  private readonly storage = inject(StorageService);
  private readonly document = inject(DOCUMENT);

  private readonly config: TranslocoConfig = inject(TRANSLOCO_CONFIG);

  readonly availableLangs: string[] = this.config.availableLangs.map((l) => (typeof l === 'string' ? l : l.id));

  private readonly _lang: WritableSignal<string> = signal<string>(this.config.defaultLang);

  readonly lang: Signal<string> = this._lang.asReadonly();

  readonly locale: Signal<string> = computed(() => LANG_TO_LOCALE[this._lang()]);

  constructor() {
    const saved = this.storage.getLocal(STORAGE_KEY);
    const initial = saved && this.availableLangs.includes(saved) ? saved : this.config.defaultLang;
    this.applyLang(initial);
  }

  setLanguage(lang: string): void {
    this.applyLang(lang);
  }

  private applyLang(lang: string): void {
    this._lang.set(lang);
    this.storage.setLocal(STORAGE_KEY, lang);
    this.translocoService.setActiveLang(lang);
    this.document.documentElement.lang = lang;
  }
}
